'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Search, MapPin, ChevronDown, Star, Shield, TrendingUp } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { getListingsStats } from '@/lib/services/listingsService';

const statusOptions = ['Buy', 'Rent', 'PG/Hostel', 'Commercial'];
const typeOptions = ['All Types', 'Apartment', 'Villa', 'Plot', 'Commercial', 'PG/Hostel'];
const locationOptions = ['All Cities', 'Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru', 'Hosur', 'Belagavi'];
const bedroomOptions = ['Any', '1 BHK', '2 BHK', '3 BHK', '4 BHK', '5+ BHK'];

const heroImages = [
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=90',
  'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1920&q=90',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=90',
];

const trendingSearches = ['3 BHK Whitefield', 'Villa HSR Layout', 'Plot Sarjapur', '2 BHK Electronic City'];

interface SiteStats {
  totalListings: number;
  totalCities: number;
  totalSellers: number;
}

export default function HeroSection() {
  const [activeStatus, setActiveStatus] = useState('Buy');
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedLocation, setSelectedLocation] = useState('All Cities');
  const [selectedBeds, setSelectedBeds] = useState('Any');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeOpen, setTypeOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [bedsOpen, setBedsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const [siteStats, setSiteStats] = useState<SiteStats>({ totalListings: 0, totalCities: 18, totalSellers: 0 });

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setImgIndex((i) => (i + 1) % heroImages.length);
    }, 6000);

    // Load dynamic stats from DB
    getListingsStats().then((stats) => {
      setSiteStats({
        totalListings: stats.totalListings,
        totalCities: stats.totalCities || 18,
        totalSellers: stats.totalSellers,
      });
    });

    return () => clearInterval(interval);
  }, []);

  const closeAll = () => { setTypeOpen(false); setLocOpen(false); setBedsOpen(false); };

  const stats = [
    { id: 'st-1', value: siteStats.totalListings > 0 ? `${(siteStats.totalListings / 1000).toFixed(0)}K+` : '52,000+', label: 'Active Listings' },
    { id: 'st-2', value: '1.2L+', label: 'Happy Users' },
    { id: 'st-3', value: `${siteStats.totalCities}+`, label: 'Cities' },
    { id: 'st-4', value: '₹2,400 Cr+', label: 'Properties Sold' },
  ];

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedLocation !== 'All Cities') params.set('city', selectedLocation);
    if (selectedType !== 'All Types') params.set('type', selectedType);
    if (selectedBeds !== 'Any') params.set('beds', selectedBeds);
    if (activeStatus !== 'Buy') params.set('status', activeStatus);
    window.location.href = `/listings-page?${params.toString()}`;
  };

  return (
    <section className="relative w-full overflow-hidden" style={{ minHeight: '100svh' }}>
      {/* Cinematic background with crossfade */}
      <div className="absolute inset-0">
        {heroImages.map((src, i) => (
          <div
            key={`hero-img-${i}`}
            className="absolute inset-0 transition-opacity duration-[2000ms] ease-in-out"
            style={{ opacity: mounted && imgIndex === i ? 1 : 0 }}
          >
            <AppImage
              src={src}
              alt={`Premium property in Karnataka - hero image ${i + 1}`}
              fill
              className="object-cover"
              priority={i === 0}
            />
          </div>
        ))}
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.22) 40%, rgba(0,0,0,0.72) 100%)'
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(90deg, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.3) 100%)'
        }} />
      </div>

      {/* Floating header nav for homepage */}
      <div className={`absolute top-0 left-0 right-0 z-30 transition-all duration-700 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-10 py-4 lg:py-5 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 rounded-[11px] bg-[#D93025] flex items-center justify-center shadow-red">
              <MapPin size={16} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-sans text-[18px] font-700 text-white tracking-tight">
              Namma<span className="text-[#FF6B6B]">Price</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {['Properties', 'Vehicles', 'Jobs', 'Services'].map((item) => (
              <Link
                key={item}
                href={`/listings-page?cat=${item.toLowerCase()}`}
                className="px-3.5 py-2 text-[13px] font-500 text-white/80 hover:text-white hover:bg-white/10 rounded-[8px] transition-all duration-150"
              >
                {item}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link
              href="/post-listing"
              className="hidden sm:flex items-center gap-1.5 bg-[#D93025] text-white px-4 py-2 rounded-[10px] text-[13px] font-600 hover:bg-[#B71C1C] transition-all shadow-red touch-manipulation active:scale-95"
            >
              <span className="text-base leading-none">+</span>
              Post Free Ad
            </Link>
            <Link
              href="/sign-up-login-screen"
              className="flex items-center gap-1.5 glass-light text-[#1D1D1F] px-3 sm:px-4 py-2 rounded-[10px] text-[12px] sm:text-[13px] font-600 hover:bg-white transition-all touch-manipulation"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 pt-20 sm:pt-24 pb-24 sm:pb-16">
        {/* Trust badge */}
        <div className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-5 sm:mb-7 transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="flex items-center gap-0.5">
            {[1,2,3,4,5].map((s) => <Star key={s} size={9} className="text-[#F59E0B]" fill="currentColor" />)}
          </div>
          <span className="text-white/90 text-[11px] sm:text-[12px] font-500">Trusted by 1.2 Lakh+ users across Karnataka</span>
          <Shield size={12} className="text-[#F59E0B] hidden sm:block" />
        </div>

        {/* Hero headline */}
        <h1
          className={`text-center text-white mb-3 sm:mb-4 transition-all duration-700 delay-150 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 'clamp(30px, 7vw, 76px)',
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            maxWidth: '960px',
          }}
        >
          Find Your Perfect Home
          <br />
          <span style={{ color: 'rgba(255,255,255,0.72)', fontWeight: 300, fontStyle: 'italic', fontFamily: 'DM Serif Display, serif' }}>
            Across Karnataka
          </span>
        </h1>

        <p
          className={`text-white/65 text-center mb-6 sm:mb-10 transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ fontSize: 'clamp(14px, 3vw, 17px)', fontWeight: 400, maxWidth: '520px', lineHeight: 1.7, letterSpacing: '-0.01em' }}
        >
          India&apos;s most trusted marketplace — buy, sell, or rent properties, vehicles, and more.
        </p>

        {/* Search Panel */}
        <div
          className={`w-full transition-all duration-700 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ maxWidth: '860px' }}
        >
          {/* Status tabs - scrollable on mobile */}
          <div className="flex gap-1 mb-0 overflow-x-auto scrollbar-hide">
            {statusOptions.map((status) => (
              <button
                key={`status-${status}`}
                onClick={() => setActiveStatus(status)}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-600 transition-all rounded-t-[10px] flex-shrink-0 touch-manipulation ${
                  activeStatus === status
                    ? 'bg-white text-[#D93025]'
                    : 'bg-white/10 text-white/80 hover:bg-white/18 backdrop-blur-sm'
                }`}
                style={{ letterSpacing: '-0.01em' }}
              >
                {status}
              </button>
            ))}
          </div>

          {/* Main search bar */}
          <div className="search-bar-dark rounded-b-[16px] rounded-tr-[16px] p-3 sm:p-5">
            {/* Row 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="sm:col-span-2 relative">
                <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1A6]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search location, project..."
                  className="search-input-premium pl-11"
                  style={{ borderRadius: '10px', fontSize: '14px' }}
                />
              </div>

              {/* Location */}
              <div className="relative">
                <button
                  onClick={() => { closeAll(); setLocOpen(!locOpen); }}
                  className="search-input-premium flex items-center justify-between px-4 cursor-pointer touch-manipulation"
                  style={{ borderRadius: '10px' }}
                >
                  <div className="flex items-center gap-2">
                    <MapPin size={13} className="text-[#A1A1A6] flex-shrink-0" />
                    <span className={selectedLocation === 'All Cities' ? 'text-[#A1A1A6]' : 'text-[#1D1D1F]'} style={{ fontSize: '14px' }}>
                      {selectedLocation}
                    </span>
                  </div>
                  <ChevronDown size={13} className={`text-[#A1A1A6] transition-transform flex-shrink-0 ${locOpen ? 'rotate-180' : ''}`} />
                </button>
                {locOpen && (
                  <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#E2E2E7] rounded-[12px] shadow-modal z-30 w-full py-1.5 animate-fade-in overflow-hidden">
                    {locationOptions.map((loc) => (
                      <button
                        key={`loc-${loc}`}
                        onClick={() => { setSelectedLocation(loc); setLocOpen(false); }}
                        className={`w-full text-left px-4 py-3 text-[13px] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors touch-manipulation ${selectedLocation === loc ? 'text-[#D93025] font-600 bg-[#FFEBEE]' : 'text-[#1D1D1F]'}`}
                      >
                        {loc}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Type */}
              <div className="relative">
                <button
                  onClick={() => { closeAll(); setTypeOpen(!typeOpen); }}
                  className="search-input-premium flex items-center justify-between px-4 cursor-pointer"
                  style={{ borderRadius: '10px' }}
                >
                  <span className={selectedType === 'All Types' ? 'text-[#A1A1A6]' : 'text-[#1D1D1F]'} style={{ fontSize: '14px' }}>
                    {selectedType}
                  </span>
                  <ChevronDown size={13} className={`text-[#A1A1A6] transition-transform flex-shrink-0 ${typeOpen ? 'rotate-180' : ''}`} />
                </button>
                {typeOpen && (
                  <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#E2E2E7] rounded-[12px] shadow-modal z-30 w-full py-1.5 animate-fade-in overflow-hidden">
                    {typeOptions.map((type) => (
                      <button
                        key={`type-${type}`}
                        onClick={() => { setSelectedType(type); setTypeOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors ${selectedType === type ? 'text-[#D93025] font-600 bg-[#FFEBEE]' : 'text-[#1D1D1F]'}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
              {/* Bedrooms */}
              <div className="relative">
                <button
                  onClick={() => { closeAll(); setBedsOpen(!bedsOpen); }}
                  className="search-input-premium flex items-center justify-between px-4 cursor-pointer"
                  style={{ borderRadius: '10px' }}
                >
                  <span className={selectedBeds === 'Any' ? 'text-[#A1A1A6]' : 'text-[#1D1D1F]'} style={{ fontSize: '14px' }}>
                    {selectedBeds === 'Any' ? 'Bedrooms' : selectedBeds}
                  </span>
                  <ChevronDown size={13} className={`text-[#A1A1A6] transition-transform flex-shrink-0 ${bedsOpen ? 'rotate-180' : ''}`} />
                </button>
                {bedsOpen && (
                  <div className="absolute top-full left-0 mt-1.5 bg-white border border-[#E2E2E7] rounded-[12px] shadow-modal z-30 w-full py-1.5 animate-fade-in overflow-hidden">
                    {bedroomOptions.map((bed) => (
                      <button
                        key={`bed-${bed}`}
                        onClick={() => { setSelectedBeds(bed); setBedsOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-[13px] hover:bg-[#F5F5F7] hover:text-[#D93025] transition-colors ${selectedBeds === bed ? 'text-[#D93025] font-600 bg-[#FFEBEE]' : 'text-[#1D1D1F]'}`}
                      >
                        {bed === 'Any' ? 'Any Bedrooms' : bed}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <input type="text" placeholder="Min. Price (₹)" className="search-input-premium" style={{ borderRadius: '10px' }} />
              <input type="text" placeholder="Max. Price (₹)" className="search-input-premium" style={{ borderRadius: '10px' }} />

              <button
                onClick={handleSearch}
                className="btn-primary w-full flex items-center justify-center gap-2"
                style={{ borderRadius: '10px', height: '48px', fontSize: '14px' }}
              >
                <Search size={15} />
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Trending searches */}
        <div className={`flex items-center gap-3 mt-5 flex-wrap justify-center transition-all duration-700 delay-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <span className="text-white/50 text-[12px] font-500 flex items-center gap-1.5">
            <TrendingUp size={12} /> Trending:
          </span>
          {trendingSearches.map((term) => (
            <Link
              key={term}
              href={`/listings-page?q=${encodeURIComponent(term)}`}
              className="text-white/75 text-[12px] font-500 hover:text-white transition-colors bg-white/8 hover:bg-white/15 px-3 py-1 rounded-full border border-white/10 hover:border-white/25"
            >
              {term}
            </Link>
          ))}
        </div>

        {/* Stats row — dynamic from DB */}
        <div
          className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mt-12 w-full transition-all duration-700 delay-600 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          style={{ maxWidth: '720px' }}
        >
          {stats.map((stat) => (
            <div key={stat.id} className="stat-card text-center">
              <p className="text-white font-700 text-[22px] leading-none mb-1 price-tag">{stat.value}</p>
              <p className="text-white/55 text-[12px] font-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-float">
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>

      {/* Image dots */}
      <div className="absolute bottom-8 right-8 z-10 flex items-center gap-1.5">
        {heroImages.map((_, i) => (
          <button
            key={`dot-${i}`}
            onClick={() => setImgIndex(i)}
            className={`rounded-full transition-all duration-300 ${imgIndex === i ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'}`}
            aria-label={`Hero image ${i + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
